import React from 'react';
import api from '../api';

/**
 * RocketChart — full-canvas engaging trading chart (Feb 2026 Phase A rebuild).
 *
 * Concept: a 🚀 rocket flying through a night sky. X-axis = time. Y-axis =
 * price (pts). The rocket sits at the rightmost point (current price) with
 * a green-or-red fire tail. Its historical trail behind it is drawn as a
 * segmented line — green when climbing, red when dipping.
 *
 * Phase A changes:
 *   • Milestones + progress bar REMOVED — no more "🎯 Next tier" clutter.
 *   • Right Y-axis shows PRICE (pts) ticks with a nice-step adaptive scale
 *     that auto-widens by decade as the price grows.
 *   • X always = time (LIVE / 1H / 24H / 7D / ALL all use timestamps).
 *   • Total Bits Minted rendered as a fixed floating label inside the chart
 *     canvas (top-left) so the info survives the milestone removal.
 *   • Zoom (+/−/Fit) buttons, mouse wheel, pinch-to-zoom, and drag-pan all
 *     preserved (Phase B will add camera-follow physics).
 */
export default function RocketChart({ points, currentPrice, currentSupply, supplyCap, holdersCount, maxPerUser, whalePct, curveType, priceUp, confetti, timeframe, launchAtIso, launchTz, clanHandle, reminderCount, onResetLive }) {
  const W = 400, H = 260;

  // Phase D — pre-launch detection + 1-second countdown ticker. When set, the
  // clan is minted but trading is locked until `launchAtIso`. Renders a
  // launchpad scene (static rocket, static stars, countdown clock) INSTEAD
  // of the flying chart. Auto-transitions to the flying chart at T=0.
  const [prelaunchMs, setPrelaunchMs] = React.useState(() => {
    if (!launchAtIso) return 0;
    const ms = new Date(launchAtIso).getTime() - Date.now();
    return ms > 0 ? ms : 0;
  });
  React.useEffect(() => {
    if (!launchAtIso) { setPrelaunchMs(0); return undefined; }
    const tick = () => {
      const ms = new Date(launchAtIso).getTime() - Date.now();
      setPrelaunchMs(ms > 0 ? ms : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [launchAtIso]);
  const isPrelaunch = prelaunchMs > 0;
  const countdownText = (() => {
    if (!isPrelaunch) return '';
    const s = Math.floor(prelaunchMs / 1000);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (days > 0) return `${days}d ${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
    if (hours > 0) return `${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
    return `${pad(mins)}m ${pad(secs)}s`;
  })();
  const launchWhen = launchAtIso ? new Date(launchAtIso) : null;
  // Two flavours of the "when" line: bold in the creator's local time (if a
  // launch_tz was set at mint), lighter secondary in the viewer's own TZ.
  const fmtOpts = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
  const launchWhenCreator = (() => {
    if (!launchWhen || !launchTz) return '';
    try { return launchWhen.toLocaleString(undefined, { ...fmtOpts, timeZone: launchTz }); }
    catch { return ''; }
  })();
  const launchWhenViewer = launchWhen ? launchWhen.toLocaleString(undefined, fmtOpts) : '';

  // Pre-launch reminder toggle — tiny bell button in the overlay corner
  // (flavor C). Talks to the same POST/DELETE /clans/{handle}/remind
  // endpoints the /launch/:id detail page already uses. Fetches current
  // state on mount so returning fans see "on the list" immediately.
  const [reminded, setReminded] = React.useState(false);
  const [remindBusy, setRemindBusy] = React.useState(false);
  const [localReminderCount, setLocalReminderCount] = React.useState(reminderCount || 0);
  // Whale-cap hint tooltip — toggled by hovering the "?" badge on the
  // clan-stats chip (desktop) or tapping it (mobile). Kept as component
  // state instead of a native <title> so we can style it (compact box,
  // multi-line, no OS default rendering) and control positioning within
  // the SVG canvas.
  const [whaleHintOpen, setWhaleHintOpen] = React.useState(false);
  React.useEffect(() => { setLocalReminderCount(reminderCount || 0); }, [reminderCount]);
  React.useEffect(() => {
    if (!isPrelaunch || !clanHandle) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/clans/${clanHandle}/remind/me`);
        if (alive && data) setReminded(!!data.you_are_reminded);
      } catch { /* endpoint may 404 for legacy clans — silent */ }
    })();
    return () => { alive = false; };
  }, [isPrelaunch, clanHandle]);
  const toggleReminder = async () => {
    if (!clanHandle || remindBusy) return;
    setRemindBusy(true);
    try {
      const url = `/clans/${clanHandle}/remind`;
      const { data } = reminded ? await api.delete(url) : await api.post(url);
      setReminded(!!data.you_are_reminded);
      if (typeof data.reminder_count === 'number') setLocalReminderCount(data.reminder_count);
    } catch { /* silent */ }
    finally { setRemindBusy(false); }
  };

  // Normalize points → {t, price, side, qty} sorted ascending; supports both
  // new (`price`) and legacy (`price_after`) backend keys.
  const cleanAll = (points || [])
    .filter((p) => p && Number.isFinite(p.price ?? p.price_after) && (p.price ?? p.price_after) > 0)
    .map((p) => ({ t: p.t, price: p.price ?? p.price_after, side: p.side, qty: p.qty || 1 }))
    .sort((a, b) => (a.t || '').localeCompare(b.t || ''));

  // Timeframe-scoped slice:
  //   LIVE → last 30 points (tight zoom on the freshest activity)
  //   1H   → filter to points in the last hour by wall-clock delta
  //   24H / 7D / ALL → use what the backend returned for that window
  const nowMs = Date.now();
  // Window duration in ms for the flat-line fill below. `null` means the
  // backend controls the window (24H/7D/ALL) or the timeframe is
  // freshness-based (LIVE).
  const WINDOW_MS = { '1H': 60 * 60 * 1000, '24H': 24 * 60 * 60 * 1000, '7D': 7 * 24 * 60 * 60 * 1000 };
  let clean = cleanAll;
  if (timeframe === 'LIVE') {
    clean = cleanAll.slice(-30);
  } else if (timeframe === '1H') {
    clean = cleanAll.filter((p) => {
      const t = p.t ? Date.parse(p.t) : 0;
      return t > 0 && nowMs - t <= WINDOW_MS['1H'];
    });
  }
  // Flat-line fill for quiet windows: if the current timeframe window has
  // no trades (or only a single point) but the clan has traded historically,
  // synthesize a "now" point at the current live price so the rocket stays
  // connected to a visible horizontal trail instead of appearing orphaned
  // in mid-air. Uses `currentPrice` (live prop) as the level — no trades
  // in this slice = price hasn't moved from where it settled. Applies to:
  //   • 1H / 24H / 7D — client- or backend-filtered, may be empty
  //   • LIVE with exactly 1 trade — anchor the rocket to that single point
  //     with a synthetic "now" tick so the trail draws (fixes the "chart
  //     shows a lone rocket with no trail" bug on brand-new Clans)
  // ALL is intentionally excluded — if that's empty the clan literally
  // has zero trades and the "Waiting for first trades…" state is right.
  const isWindowedQuiet = WINDOW_MS[timeframe] && clean.length < 2 && cleanAll.length >= 1;
  const isLiveSinglePoint = timeframe === 'LIVE' && clean.length === 1;
  if (
    (isWindowedQuiet || isLiveSinglePoint)
    && Number.isFinite(currentPrice)
    && currentPrice > 0
  ) {
    if (isLiveSinglePoint) {
      // Anchor from the one real trade → synthetic "now" tick at currentPrice.
      // Preserves the real trade's timestamp so the trail slope is truthful.
      clean = [
        ...clean,
        { t: new Date(nowMs).toISOString(), price: currentPrice, side: null, qty: 0, synthetic: true },
      ];
    } else {
      const windowStartMs = nowMs - WINDOW_MS[timeframe];
      clean = [
        { t: new Date(windowStartMs).toISOString(), price: currentPrice, side: null, qty: 0, synthetic: true },
        ...clean,
        { t: new Date(nowMs).toISOString(),          price: currentPrice, side: null, qty: 0, synthetic: true },
      ];
    }
  }

  // ---- Zoom + Pan state ---------------------------------------------
  // `viewStart`/`viewEnd` express the visible fraction of the timeframe
  // window (0..1). Defaults to full ([0, 1]) → "Fit trail" state that
  // autoscales to exactly the buys-so-far as the user requested. Wheel
  // + pinch adjust the window; the +/− buttons zoom around the center;
  // "Fit" resets to [0, 1]. Reset happens whenever the timeframe changes
  // so tab-swaps feel clean.
  const [viewStart, setViewStart] = React.useState(0);
  const [viewEnd, setViewEnd] = React.useState(1);
  // Ground-up Y-axis is now the DEFAULT for every timeframe — the rocket
  // sitting on 0 with the full climb visible above is the mental model
  // fans have (they think "how far up did it fly?", not "what's the
  // narrow band right now?"). Camera-follow was hiding the starting
  // point of the rocket, so we retired it. The `forceGroundUp` state
  // remains as a hook for the Reset button but starts true and stays
  // true unless the user explicitly zooms the Y-axis via wheel/pinch.
  const [forceGroundUp, setForceGroundUp] = React.useState(true);
  React.useEffect(() => { setViewStart(0); setViewEnd(1); setForceGroundUp(true); }, [timeframe]);
  const zoomedPct = 1 - Math.min(1, viewEnd - viewStart); // 0 = fit, 1 = max zoom
  const isFit = viewStart === 0 && viewEnd === 1;

  // Apply zoom+pan to the `clean` slice. Uses fractional indexing so the
  // curve stays smooth even at deep zoom on sparse data.
  const applyView = (arr) => {
    if (arr.length < 2) return arr;
    const s = Math.max(0, Math.min(arr.length - 1, Math.floor(viewStart * (arr.length - 1))));
    const e = Math.max(s + 1, Math.min(arr.length, Math.ceil(viewEnd * (arr.length - 1)) + 1));
    return arr.slice(s, e);
  };
  const visible = applyView(clean);
  const isTightZoom = timeframe === 'LIVE' || timeframe === '1H' || zoomedPct > 0.4;

  // Wheel + pinch handlers — attached to the outer .rc-wrap div. `zoomAt`
  // preserves the fractional cursor position when zooming (i.e. zoom
  // toward the cursor rather than the center) so the interaction feels
  // like the cursor is anchored — same UX as TradingView.
  const wrapRef = React.useRef(null);
  const zoomBy = React.useCallback((factor, anchorFrac) => {
    // Compute new [start, end] atomically so we don't leak an intermediate
    // state. Uses the current viewStart/viewEnd captured in the closure —
    // fine because zoomBy is memo'd against those exact deps below.
    const currentSpan = viewEnd - viewStart;
    const newSpan = Math.max(0.02, Math.min(1, currentSpan * factor));
    const anchor = viewStart + currentSpan * anchorFrac;
    let newStart = anchor - newSpan * anchorFrac;
    let newEnd = newStart + newSpan;
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > 1) { newStart -= (newEnd - 1); newEnd = 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(1, newEnd);
    setViewStart(newStart); setViewEnd(newEnd);
  }, [viewStart, viewEnd]);

  React.useEffect(() => {
    const el = wrapRef.current; if (!el) return undefined;
    const onWheel = (e) => {
      // Wheel on the chart = X-axis zoom (same behavior as the +/- buttons).
      // We deliberately DO NOT touch the Y-axis (yZoom stays 1) so the ground
      // baseline at price=0 remains locked to the bottom of the canvas — this
      // is what the user asked for: "if we see 0 on Y-axis, we always see the
      // rocket's starting point". Modifier keys (ctrl/cmd) pass through so
      // browser zoom still works.
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      zoomBy(factor, frac);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  // Pinch-to-zoom for touch devices. Tracks two touch points, computes
  // the distance ratio between move events and zooms the X-axis window
  // (same behavior as +/- buttons and wheel). Anchor is the midpoint of
  // the two fingers so the pinch center stays put.
  React.useEffect(() => {
    const el = wrapRef.current; if (!el) return undefined;
    let startDist = null;
    let anchorFrac = 0.5;
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const onTouchStart = (e) => {
      if (e.touches.length !== 2) return;
      startDist = dist(e.touches[0], e.touches[1]);
      const rect = el.getBoundingClientRect();
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      anchorFrac = Math.max(0, Math.min(1, (midX - rect.left) / rect.width));
    };
    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || startDist == null) return;
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / startDist;
      // Only fire on non-trivial pinch to avoid jitter.
      if (Math.abs(ratio - 1) < 0.04) return;
      zoomBy(1 / ratio, anchorFrac);
      startDist = d;
    };
    const onTouchEnd = () => { startDist = null; };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [zoomBy]);

  const fitTrail = () => {
    setViewStart(0); setViewEnd(1); setYZoom(1); setForceGroundUp(true);
    // Reset also flips the parent's timeframe back to LIVE so the rocket
    // returns to its live anchor position — matches the user's mental
    // model of "reset means back to live state".
    if (typeof onResetLive === 'function') onResetLive();
  };
  const zoomInBtn = () => zoomBy(1 / 1.5, 0.5);
  const zoomOutBtn = () => zoomBy(1.5, 0.5);

  // --- Drag-pan the chart when zoomed in --------------------------------
  // When the user has zoomed in (viewStart > 0 or viewEnd < 1) they can
  // grab the trail and sweep left/right to sweep through flight history.
  // Delta X in pixels → shift in fractional units of the FULL dataset,
  // clamped so the window never leaves [0, 1]. Mouse drag on desktop and
  // single-finger drag on touch. Two-finger touches are still routed to
  // the pinch-zoom effect above (they exit the drag logic early).
  const [dragging, setDragging] = React.useState(false);
  React.useEffect(() => {
    const el = wrapRef.current; if (!el) return undefined;
    // Panning is only meaningful when zoomed in. If we're at fit-trail
    // there's nothing left/right of the visible window to reveal, so we
    // skip attaching the pan listeners entirely (also avoids competing
    // with any parent gestures — e.g. the AppShell swipe-up-to-reveal
    // tabbar handler that fires on touches starting near the viewport
    // bottom).
    if (isFit) return undefined;

    let startX = null; let startView = null; let widthPx = 0;
    const beginDrag = (clientX) => {
      startX = clientX;
      startView = { s: viewStart, e: viewEnd };
      widthPx = el.getBoundingClientRect().width || 1;
      setDragging(true);
    };
    const moveDrag = (clientX) => {
      if (startX == null) return;
      const currentSpan = startView.e - startView.s;
      // Drag RIGHT → reveal EARLIER trail → viewStart shrinks (negative delta).
      const deltaFrac = -((clientX - startX) / widthPx) * currentSpan;
      let s = startView.s + deltaFrac;
      let e = startView.e + deltaFrac;
      if (s < 0) { e -= s; s = 0; }
      if (e > 1) { s -= (e - 1); e = 1; }
      setViewStart(Math.max(0, s));
      setViewEnd(Math.min(1, e));
    };
    const endDrag = () => { startX = null; startView = null; setDragging(false); };

    // Mouse (desktop)
    const onMouseDown = (e) => {
      // Ignore clicks on the zoom-control buttons so they still work.
      if (e.target.closest('.rc-zoom-ctrls')) return;
      if (e.button !== 0) return;
      beginDrag(e.clientX);
    };
    const onMouseMove = (e) => { if (startX != null) moveDrag(e.clientX); };
    const onMouseUp = () => endDrag();

    // Touch (mobile) — single-finger only. If a second finger lands mid-drag,
    // bail out so the pinch-zoom handler takes over cleanly.
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      if (e.target.closest('.rc-zoom-ctrls')) return;
      beginDrag(e.touches[0].clientX);
    };
    const onTouchMove = (e) => {
      if (e.touches.length !== 1 || startX == null) { endDrag(); return; }
      e.preventDefault();
      moveDrag(e.touches[0].clientX);
    };
    const onTouchEnd = () => endDrag();

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isFit, viewStart, viewEnd]);

  // Y-scale (price) — Phase B camera-follow: the Y-axis auto-centers so the
  // CURRENT PRICE always sits at rocket-Y (viewport middle). Y-range grows
  // just enough to contain the visible trail on either side of the current
  // price. This is what makes the rocket read as "glued to the middle."
  //
  // EXCEPTION for wide-timeframe views (24H / 7D / ALL): anchor yMin = 0
  // (ground-up mode) so the earliest low-price point visibly sits near the
  // bottom of the chart and the rocket reads as "climbed from ground zero"
  // — the actual expected mental model for a bonding curve. Camera-follow
  // is still used for LIVE / 1H where tight recent moves need drama.
  //
  // `yZoom` is a user-adjustable multiplier (>1 = zoom-in, expands Y-range
  // per pixel; <1 = zoom-out). Adjusted via wheel-over-Y-axis and via a
  // vertical pinch gesture — see the wheel + touch handlers below.
  const [yZoom, setYZoom] = React.useState(1);
  React.useEffect(() => { setYZoom(1); }, [timeframe]);
  const priceSeries = visible.map((p) => p.price);
  const anchor = currentPrice > 0 ? currentPrice : (priceSeries[priceSeries.length - 1] || 0);
  const seenMax = priceSeries.length ? Math.max(...priceSeries, anchor) : anchor;
  const seenMin = priceSeries.length ? Math.min(...priceSeries, anchor) : anchor;
  const isWideView = timeframe === '24H' || timeframe === '7D' || timeframe === 'ALL';
  const groundUp = isWideView || forceGroundUp;
  let yMinRaw, yMaxRaw;
  if (groundUp && priceSeries.length > 0) {
    // Ground-up: floor at 0, ceiling ~12% above the highest observed price
    // so the trail always sits inside the frame with a little headroom.
    yMinRaw = 0;
    yMaxRaw = Math.max(seenMax * 1.12, anchor * 1.12, 0.0001);
  } else if (forceGroundUp && priceSeries.length === 0) {
    // Reset pressed on an empty window (e.g. 1H when the last trade is
    // >1h old). Honor the user's ask anyway — floor at 0, synthesize a
    // ceiling around the last known price so the axis has something to
    // draw against and the rocket isn't stuck floating in undefined
    // territory.
    yMinRaw = 0;
    yMaxRaw = Math.max(anchor * 1.25, 0.0001);
  } else {
    // Camera-follow (LIVE / 1H): symmetric half-range around current price.
    const halfRange = Math.max(
      anchor - seenMin,
      seenMax - anchor,
      anchor * (isTightZoom ? 0.04 : 0.08),  // minimum breathing room
      0.0001,
    );
    yMinRaw = Math.max(0, anchor - halfRange * 1.10);
    yMaxRaw = anchor + halfRange * 1.10;
  }
  // Apply user Y-zoom around the anchor (currentPrice). yZoom > 1 = shrink
  // the visible range (visually magnifies price moves); yZoom < 1 = expand
  // (compress). yZoom = 1 → unchanged.
  //
  // CRITICAL: In ground-up mode we PIN yMin = 0 regardless of yZoom. This
  // guarantees the "0" tick is always visible at the bottom of the chart
  // — the user's explicit ask ("if we see 0 on Y-axis, we always see the
  // rocket's starting point"). yZoom only affects the upper bound.
  const yCenter = anchor;
  const yMin = groundUp ? 0 : Math.max(0, yCenter - (yCenter - yMinRaw) / yZoom);
  const yMax = yCenter + (yMaxRaw - yCenter) / yZoom;
  const yRange = yMax - yMin;
  const yFor = (price) => H - 40 - ((price - yMin) / yRange) * (H - 90);

  // Right-axis PRICE ticks — adaptive decade-based scale (Phase A). Uses a
  // nice-step algorithm (1/2/5 × 10^k) so we always render 4-6 clean ticks
  // regardless of the zoom level. When yMin is 0 and yMax is 1 pt, ticks
  // land at 0.1 intervals; when the view spans 0-1000 pts, ticks are at
  // 100 intervals; etc. Matches the "10-part decade" reference the user
  // sketched, just applied to the price dimension.
  const niceStep = (v) => {
    if (v <= 0) return 1;
    const exp = Math.floor(Math.log10(v));
    const mag = Math.pow(10, exp);
    const norm = v / mag;
    if (norm < 1.5) return mag;
    if (norm < 3.5) return mag * 2;
    if (norm < 7.5) return mag * 5;
    return mag * 10;
  };
  const priceAxis = [];
  if (yMax > yMin) {
    const step = niceStep(yRange / 4.5);
    const first = Math.ceil(yMin / step) * step;
    for (let p = first; p <= yMax * 1.001 && priceAxis.length < 6; p += step) {
      priceAxis.push(p);
    }
    if (priceAxis.length === 0) priceAxis.push(anchor);
    // ALWAYS include 0 when yMin is 0 (ground-up mode). Prevents the
    // niceStep loop from dropping the ground marker on non-round ranges.
    if (yMin === 0 && !priceAxis.includes(0)) priceAxis.unshift(0);
  }

  const fmtNum = (n) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : `${Math.round(n)}`);

  // --- Rocket kinematics (Phase B + C) -----------------------------------
  // Rocket is glued to viewport center. On each new trade a size-proportional
  // IMPULSE is applied to a velocity → the rocket is briefly displaced up
  // (buys) or down (sells), plus a small forward (right) nudge. A spring
  // continuously pulls the offset back to zero with damping, so consecutive
  // small trades cluster around the middle while big trades launch the ship
  // higher/lower. Clamps guarantee the rocket never crosses 90% of the
  // viewBox width or merges into the right-hand Y axis column.
  const ROCKET_X_CENTER = W * 0.35;   // 5 more notches left → ~35% width, lots of runway on the right
  const ROCKET_Y_CENTER = (30 + (H - 40)) / 2;  // ~125 for H=260
  const ROCKET_LEAD = 0;              // rocket sits AT the trail's last point — visually IN FRONT of the curve
  const MAX_Y_OFFSET = 55;   // px — ~21% of H, well inside drawable band
  const MAX_X_OFFSET = 100;  // px — bigger propulsion runway now that rocket sits at 35% W
  const TRAIL_END_X = ROCKET_X_CENTER;  // trail ends AT the rocket → attached like the reference image
  const [thrust, setThrust] = React.useState({ side: null, power: 0 });
  const [phys, setPhys] = React.useState({ x: 0, y: 0 });
  const physRef = React.useRef({ x: 0, y: 0, vx: 0, vy: 0, last: 0 });

  // Physics tick — spring pull-back to (0, 0) with damping. Runs continuously
  // via requestAnimationFrame. Cheap; only touches state when position moves.
  React.useEffect(() => {
    let raf;
    const K = 5.5;   // spring stiffness (higher = snappier return)
    const C = 3.8;   // damping (higher = fewer oscillations)
    const tick = (t) => {
      const s = physRef.current;
      if (s.last === 0) s.last = t;
      const dt = Math.min(0.06, (t - s.last) / 1000);  // clamp big frames after tab-suspend
      s.last = t;
      // F = -K*x - C*v for each axis (harmonic spring)
      s.vy += (-K * s.y - C * s.vy) * dt;
      s.vx += (-K * s.x - C * s.vx) * dt;
      s.y += s.vy * dt;
      s.x += s.vx * dt;
      // Clamp position + damp velocity when hitting the wall.
      if (s.y > MAX_Y_OFFSET) { s.y = MAX_Y_OFFSET; s.vy *= -0.35; }
      if (s.y < -MAX_Y_OFFSET) { s.y = -MAX_Y_OFFSET; s.vy *= -0.35; }
      if (s.x > MAX_X_OFFSET) { s.x = MAX_X_OFFSET; s.vx *= -0.35; }
      if (s.x < -MAX_X_OFFSET) { s.x = -MAX_X_OFFSET; s.vx *= -0.35; }
      // Auto-settle at tiny values so we don't burn cycles on 0.001px wiggles.
      if (Math.abs(s.y) < 0.03 && Math.abs(s.vy) < 0.03) { s.y = 0; s.vy = 0; }
      if (Math.abs(s.x) < 0.03 && Math.abs(s.vx) < 0.03) { s.x = 0; s.vx = 0; }
      // Skip the setState (and the resulting subtree re-render) when we're
      // idle at the origin — otherwise the RAF loop would trigger ~60Hz
      // no-op renders on the whole RocketChart between trades.
      if (s.x === 0 && s.y === 0 && s.vx === 0 && s.vy === 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      setPhys({ x: s.x, y: s.y });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const prevLenRef = React.useRef(visible.length);
  React.useEffect(() => {
    if (visible.length > prevLenRef.current && prevLenRef.current > 0) {
      const newest = visible[visible.length - 1];
      const prev = visible[visible.length - 2];
      const isUp = (newest?.side === 'buy') || (newest?.price >= (prev?.price || 0));
      const qty = Math.max(1, Number(newest?.qty || 1));
      // Impulse magnitude — log-scaled so 1-Bit trades still register but
      // 100-Bit whales feel WAY more dramatic. 1 Bit ≈ 0.15, 10 ≈ 0.5,
      // 100 ≈ 1.0, capped at 1.0. Multiplied by 260 px/sec of velocity
      // → a 100-Bit buy launches the rocket ~50px up in the first frame.
      const magnitude = Math.min(1, Math.log10(1 + qty) / 2);
      const yImpulse = magnitude * 260;
      const xImpulse = magnitude * 90;  // small forward propulsion too
      physRef.current.vy += isUp ? -yImpulse : yImpulse;
      physRef.current.vx += xImpulse;
      // CSS flame flicker class — kept from Phase A/B for visual feel.
      const power = Math.min(1, magnitude);
      setThrust({ side: isUp ? 'up' : 'down', power });
      const timer = setTimeout(() => setThrust({ side: null, power: 0 }), 520);
      prevLenRef.current = visible.length;
      return () => clearTimeout(timer);
    }
    prevLenRef.current = visible.length;
    return undefined;
  }, [visible.length]);

  // Effective rocket position: trail-end + a fixed lead offset + kinetic
  // thrust nudge. The lead offset is 40px so on desktop widescreens the
  // Effective rocket position (Phase C): X/Y offset by the physics spring.
  // Phase D: during pre-launch the rocket is PARKED on the pad — static
  // at bottom-center of the canvas, ignoring physics.
  //
  // The rocket's base Y sits ON the current price line (`yFor(currentPrice)`)
  // so it visually leads the trail — no matter whether the Y-scale is in
  // camera-follow mode (LIVE / 1H — currentPrice near middle) or ground-up
  // mode (24H / 7D / ALL — currentPrice sits high on the canvas). Physics
  // impulses (spring `phys.y`) add a temporary vertical nudge on top.
  const trailEndX = TRAIL_END_X;
  const rocketX = isPrelaunch ? W * 0.5 : ROCKET_X_CENTER + phys.x;
  const rocketBaseY = yFor(currentPrice);
  // Clamp so the rocket never crosses the top gutter (30px) or clips into
  // the X-axis strip at the bottom (H-30). The physics offset can push it
  // by up to MAX_Y_OFFSET, so respect that in the clamp.
  const rocketYUnclamped = isPrelaunch ? H - 34 : rocketBaseY + phys.y;
  // The rocket icon extends ~20px above its base transform; the flame
  // reaches ~+34px below. Clamp so the entire rocket-plus-flame group
  // stays fully inside the visible chart (top: 60, bottom: H − 58 above
  // the x-axis strip at H-22) — never overlapping the x-axis or clipping
  // below the visible canvas. This addresses the "grass" the user saw at
  // the bottom of the chart when the price was near yMin.
  const rocketY = Math.max(60, Math.min(H - 58, rocketYUnclamped));

  // NOTE: Continuous JS drift on the trail was removed at the user's request
  // (they want the rocket to sit VISUALLY IN FRONT of the trail, no gap).
  // The illusion of forward motion is still carried by the CSS-driven
  // parallax star drift running behind the trail (.rc-star-drift-*).
  const driftPx = 0;

  // Trail: only draw the most recent points that fit in [24..trailEndX]. Older
  // points get compressed toward the left. This lets bursts of trades show
  // as a dense fan behind the rocket without pushing it off-screen.
  const trailWidth = trailEndX - 24;
  const xForTrail = (i, len) => 24 + (i / Math.max(1, len - 1)) * trailWidth;
  const hasTrail = visible.length >= 2;
  // A "quiet" window = every visible point is synthetic (flat line only, no
  // real trades happened here). Used to show the friendly "No trades in
  // this window" chip alongside the drawn flat line.
  const isQuietWindow = hasTrail && visible.every((p) => p.synthetic);

  const rocketTone = priceUp ? 'up' : 'down';
  const thrustClass = thrust.side ? `rc-rocket-thrust-${thrust.side}` : '';

  return (
    <div
      className={`rc-wrap${!isFit ? ' rc-pannable' : ''}${dragging ? ' rc-dragging' : ''}${isPrelaunch ? ' rc-prelaunch' : ''}`}
      data-testid="rocket-chart"
      ref={wrapRef}
    >
      {isPrelaunch && (
        <div className="rc-prelaunch-overlay" data-testid="rc-prelaunch-overlay">
          {clanHandle && (
            <button
              type="button"
              className={`rc-prelaunch-bell${reminded ? ' active' : ''}`}
              onClick={toggleReminder}
              disabled={remindBusy}
              aria-pressed={reminded}
              aria-label={reminded ? 'Remove launch reminder' : 'Notify me at launch'}
              title={
                reminded
                  ? `You're on the list — ${localReminderCount} reminded · tap to remove`
                  : `Notify me at launch · ${localReminderCount} already reminded`
              }
              data-testid="rc-prelaunch-bell"
            >
              🔔
              {localReminderCount > 0 && (
                <span className="rc-prelaunch-bell-count">{localReminderCount}</span>
              )}
            </button>
          )}
          <div className="rc-prelaunch-tag" aria-hidden="true">🚀 SCHEDULED LAUNCH</div>
          <div className="rc-prelaunch-countdown" data-testid="rc-prelaunch-countdown">
            {countdownText}
          </div>
          <div className="rc-prelaunch-when">
            Trading opens · <b>{launchWhenCreator || launchWhenViewer}</b>
          </div>
          <div className="rc-prelaunch-sub">Ready for the ride?</div>
        </div>
      )}
      {/* Zoom controls — hidden during pre-launch since there's no trail to zoom on. */}
      {!isPrelaunch && (
      <div className="rc-zoom-ctrls" data-testid="rc-zoom">
        <button type="button" className="rc-zoom-btn" onClick={zoomOutBtn}
          title="Zoom out" aria-label="Zoom out" data-testid="rc-zoom-out">−</button>
        <button type="button" className={`rc-zoom-btn rc-zoom-reset${(isFit && forceGroundUp) ? ' active' : ''}`}
          onClick={fitTrail}
          title="Reset view — show the rocket's start point with the Y-axis at zero"
          aria-label="Reset chart view"
          data-testid="rc-zoom-reset">
          {/* Refresh / reset-view arrow */}
          <svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
            <path d="M2.2 7a4.8 4.8 0 1 1 1.6 3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 4v3h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button type="button" className="rc-zoom-btn" onClick={zoomInBtn}
          title="Zoom in" aria-label="Zoom in" data-testid="rc-zoom-in">+</button>
      </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} className="rc-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="rc-sky" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0B1120" />
            <stop offset="55%" stopColor="#111827" />
            <stop offset="100%" stopColor="#1B1F29" />
          </linearGradient>
          <linearGradient id="rc-trail-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#B4FF00" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#B4FF00" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="rc-glow">
            <stop offset="0%" stopColor="#FFB020" stopOpacity="0.55" />
            <stop offset="60%" stopColor="#FFB020" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#FFB020" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={W} height={H} fill="url(#rc-sky)" />

        {/* 30 parallax stars — split into 3 layers moving at different speeds.
            Continuous leftward drift simulates the rocket flying forward. */}
        {[
          [10, 25, 0], [45, 15, 1], [80, 40, 2], [120, 8, 0], [160, 55, 1], [200, 20, 2],
          [240, 45, 0], [280, 12, 1], [320, 38, 2], [360, 25, 0], [395, 50, 1],
          [15, 80, 2], [55, 105, 0], [100, 90, 1], [145, 115, 2], [190, 78, 0],
          [235, 108, 1], [275, 92, 2], [315, 118, 0], [360, 82, 1], [395, 100, 2],
          [8, 155, 0], [50, 175, 1], [95, 148, 2], [140, 172, 0], [185, 158, 1],
          [230, 178, 2], [275, 150, 0], [320, 168, 1], [365, 155, 2], [395, 172, 0],
        ].map(([x, y, layer], i) => (
          <circle key={i} cx={x} cy={y}
            r={layer === 0 ? 1.6 : layer === 1 ? 1.1 : 0.7}
            fill="#fff"
            className={`rc-star rc-star-l${layer}`}
            style={{ animationDelay: `${(i * 0.13) % 3}s` }}
          />
        ))}

        {/* Right Y-axis: price ticks — hidden during pre-launch. */}
        {!isPrelaunch && (
          <>
            <line x1={W - 44} x2={W - 44} y1={30} y2={H - 24}
              stroke="rgba(180,255,0,0.32)" strokeWidth="1" />
            {priceAxis.map((p) => {
          if (p < yMin || p > yMax) return null;
          const ty = yFor(p);
          const label = p === 0
            ? '0'
            : p >= 1000
              ? `${(p / 1000).toFixed(p >= 10000 ? 0 : 1)}K`
              : p >= 100
                ? p.toFixed(0)
                : p >= 10
                  ? p.toFixed(1)
                  : p >= 1
                    ? p.toFixed(2)
                    : p.toFixed(3);
          return (
            <g key={`price-${p}`} className="rc-supply-tick">
              <line x1={W - 48} x2={W - 40} y1={ty} y2={ty} stroke="rgba(180,255,0,0.55)" strokeWidth="1" />
              <text x={W - 36} y={ty + 3} fill="rgba(180,255,0,0.85)" fontSize="9" fontWeight="700"
                textAnchor="start" letterSpacing="0.02em">
                {label}
              </text>
            </g>
          );
        })}
        <text x={W - 42} y={22} fill="rgba(180,255,0,0.95)" fontSize="9" fontWeight="800"
          textAnchor="start" letterSpacing="0.14em" data-testid="rc-yaxis-title">
          PRICE
        </text>
        <text x={W - 42} y={34} fill="rgba(180,255,0,0.55)" fontSize="7.5" fontWeight="700"
          textAnchor="start" letterSpacing="0.06em">
          pts
        </text>
        </>
        )}

        {/* Floating clan-stats chip — packs three at-a-glance metrics into
            one pill above the chart canvas:
              • Total Bits minted
              • Holders count (users with bits_owned >= 1, NOT members)
              • Live whale cap (max bits/holder) — for sqrt clans this is
                DYNAMIC: max(100, bits_minted × whale_pct). Grows with the
                community. A "?" hint after the number reveals a tooltip
                explaining the growth mechanic when hovered/tapped.
            Hidden during pre-launch. SVG-native rect+text (no foreignObject)
            because Safari on iOS drops transformed foreignObject subtrees.
            Muted off-white palette to recede visually — the chart already
            leans heavily on lime green and this info is reference-only. */}
        {!isPrelaunch && (() => {
          const parts = [`${fmtNum(currentSupply)} Bits minted`];
          if (typeof holdersCount === 'number' && holdersCount >= 0) {
            parts.push(`${fmtNum(holdersCount)} holder${holdersCount === 1 ? '' : 's'}`);
          }
          const showWhale = typeof maxPerUser === 'number' && maxPerUser > 0;
          if (showWhale) {
            parts.push(`${fmtNum(maxPerUser)} bits max/holder`);
          }
          const text = parts.join(' · ');
          // Empirical width per char at fontSize=9, fontWeight=500,
          // letterSpacing=0.02em. Padding = 16px total (8 each side).
          // Extra 14px on the right when the "?" badge is shown.
          const charPx = 4.8;
          const isSqrt = curveType === 'sqrt';
          const showHint = showWhale && isSqrt;
          const rectW = Math.max(128, Math.round(text.length * charPx + 16 + (showHint ? 14 : 0)));
          const textEndX = 8 + Math.round(text.length * charPx);
          const wp = typeof whalePct === 'number' && whalePct > 0 ? whalePct : 0.05;
          const wpLabel = `${(wp * 100).toFixed(wp < 0.01 ? 2 : wp < 0.1 ? 1 : 0)}%`;
          return (
            <g className="rc-minted-label" transform="translate(12, 22)" data-testid="rc-clan-stats-chip">
              <rect x="0" y="-12" width={rectW} height="17" rx="8.5" ry="8.5"
                fill="rgba(28,32,44,0.72)" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
              <text x="8" y="0" fill="rgba(230,233,242,0.78)" fontSize="9" fontWeight="500" letterSpacing="0.02em">
                {text}
              </text>
              {showHint && (
                <g
                  transform={`translate(${textEndX + 6}, -4)`}
                  style={{ cursor: 'help' }}
                  data-testid="rc-whale-cap-hint"
                  onMouseEnter={() => setWhaleHintOpen(true)}
                  onMouseLeave={() => setWhaleHintOpen(false)}
                  onClick={() => setWhaleHintOpen((v) => !v)}
                >
                  <circle cx="0" cy="0" r="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
                  <text x="0" y="2.5" textAnchor="middle" fill="rgba(230,233,242,0.9)" fontSize="8" fontWeight="700">?</text>
                </g>
              )}
              {/* Custom SVG tooltip box — compact, wraps to two lines. Painted
                  after the chip so it stacks visually on top. */}
              {showHint && whaleHintOpen && (
                <g transform="translate(0, 12)" data-testid="rc-whale-cap-tooltip" style={{ pointerEvents: 'none' }}>
                  <rect x="0" y="0" width="188" height="34" rx="6" ry="6"
                    fill="rgba(16,20,28,0.96)" stroke="rgba(255,255,255,0.32)" strokeWidth="1" />
                  <text x="10" y="14" fill="rgba(230,233,242,0.98)" fontSize="10" fontWeight="600">
                    Whale cap = {wpLabel} of supply
                  </text>
                  <text x="10" y="27" fill="rgba(230,233,242,0.65)" fontSize="9" fontWeight="400">
                    Min 100 Bits · grows with the Clan
                  </text>
                </g>
              )}
            </g>
          );
        })()}

        {/* Ground baseline — a dashed lime line pinned at Y=0 across the
            chart width. This is the "PRICE = 0" anchor the user asked for
            (per the reference sketch showing a house at ground level). It
            makes the rocket's starting point visually meaningful: the
            trail's first data point sits above this line, and the line
            NEVER moves regardless of price movements. Hidden during
            pre-launch when the rocket is parked on the pad. The "0" label
            itself lives on the right Y-axis (see priceAxis). */}
        {!isPrelaunch && yMin === 0 && (
          <g className="rc-ground" data-testid="rc-ground">
            <line x1="24" x2={W - 48}
              y1={yFor(0)} y2={yFor(0)}
              stroke="rgba(180,255,0,0.35)" strokeWidth="1"
              strokeDasharray="3 3" />
          </g>
        )}

        {/* Trail — hidden while pre-launch (rocket is parked, no history yet). */}
        {!isPrelaunch && (
        <g className="rc-trail-drift" transform={`translate(${-driftPx}, 0)`}>
          {hasTrail && (
            <path
              d={`M 24 ${H - 22} ` +
                visible.map((p, i) => `L ${xForTrail(i, visible.length)} ${yFor(p.price)}`).join(' ') +
                ` L ${xForTrail(visible.length - 1, visible.length)} ${H - 22} Z`}
              fill="url(#rc-trail-fill)"
              data-testid="rc-trail-fill"
            />
          )}
          {hasTrail && visible.slice(1).map((p, i) => {
            const prev = visible[i];
            const seg_up = p.price >= prev.price;
            return (
              <line key={`seg-${i}`}
                x1={xForTrail(i, visible.length)} y1={yFor(prev.price)}
                x2={xForTrail(i + 1, visible.length)} y2={yFor(p.price)}
                stroke={seg_up ? '#B4FF00' : '#FF5470'}
                strokeWidth="2.4" strokeLinecap="round" />
            );
          })}
          {/* Start-point marker — a small ring at the trail's first data
              point so the user has a clear "you started here" anchor. Only
              rendered on the ALL timeframe: on LIVE/1H/24H/7D the visible
              slice is a rolling window, so the "first" point is arbitrary
              and would appear to "ride along" with the rocket instead of
              staying pinned to the true launch. */}
          {hasTrail && timeframe === 'ALL' && (() => {
            const startX = xForTrail(0, visible.length);
            const startY = yFor(visible[0].price);
            return (
              <g className="rc-start-marker" data-testid="rc-start-marker">
                <circle cx={startX} cy={startY} r="4.5"
                  fill="rgba(11,13,18,0.9)" stroke="#B4FF00" strokeWidth="1.6" />
                <circle cx={startX} cy={startY} r="1.8" fill="#B4FF00" />
              </g>
            );
          })()}
        </g>
        )}

        {/* Launchpad graphic — visible only in pre-launch. Small rectangle
            + tilted flame stripes to feel like a rocket resting on a pad. */}
        {isPrelaunch && (
          <g className="rc-launchpad" data-testid="rc-launchpad">
            <rect x={W * 0.5 - 34} y={H - 14} width="68" height="8" rx="2" ry="2"
              fill="rgba(180,255,0,0.22)" stroke="rgba(180,255,0,0.55)" strokeWidth="1" />
            <line x1={W * 0.5 - 20} y1={H - 6} x2={W * 0.5 - 30} y2={H}
              stroke="rgba(180,255,0,0.45)" strokeWidth="1.4" strokeLinecap="round" />
            <line x1={W * 0.5 + 20} y1={H - 6} x2={W * 0.5 + 30} y2={H}
              stroke="rgba(180,255,0,0.45)" strokeWidth="1.4" strokeLinecap="round" />
          </g>
        )}

        {/* Rocket glow */}
        <circle cx={rocketX} cy={rocketY} r="34" fill="url(#rc-glow)" className="rc-rocket-glow" />

        {/* X-axis time labels — hidden during pre-launch (no trail yet). */}
        {!isPrelaunch && (() => {
          if (visible.length < 2) return null;
          const firstT = Date.parse(visible[0].t || '') || nowMs;
          const lastT  = Date.parse(visible[visible.length - 1].t || '') || nowMs;
          const spanMs = Math.max(1, lastT - firstT);
          const spanMin = spanMs / 60000;
          const fmtDelta = (deltaMs) => {
            const s = Math.round(deltaMs / 1000);
            if (s < 90) return `${s}s`;
            const m = Math.round(s / 60);
            if (m < 90) return `${m}m`;
            const h = Math.round(m / 60);
            if (h < 36) return `${h}h`;
            const d = Math.round(h / 24);
            return `${d}d`;
          };
          const fmtAbs = (ms) => {
            const dt = new Date(ms);
            if (spanMin < 90) return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            if (spanMin < 60 * 24 * 3) return dt.toLocaleTimeString([], { hour: 'numeric' });
            return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
          };
          const ticks = [
            { frac: 0, anchor: 'start' },
            { frac: 0.5, anchor: 'middle' },
            { frac: 1, anchor: 'end' },
          ].map(({ frac, anchor }) => {
            const x = 24 + frac * (TRAIL_END_X - 24);
            const t = firstT + frac * spanMs;
            const label = (timeframe === 'LIVE' || timeframe === '1H')
              ? (frac === 1 ? 'now' : `${fmtDelta(lastT - t)} ago`)
              : fmtAbs(t);
            return { x, anchor, label };
          });
          return (
            <g className="rc-xaxis">
              <line x1="24" x2={TRAIL_END_X + 8} y1={H - 22} y2={H - 22}
                stroke="rgba(150,200,255,0.14)" strokeWidth="1" />
              {ticks.map((tick, i) => (
                <g key={`t-${i}`}>
                  <line x1={tick.x} x2={tick.x} y1={H - 22} y2={H - 19}
                    stroke="rgba(150,200,255,0.28)" strokeWidth="1" />
                  <text x={tick.x} y={H - 10} fill="rgba(150,200,255,0.55)"
                    fontSize="8" fontWeight="600" textAnchor={tick.anchor} letterSpacing="0.02em">
                    {tick.label}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}

        {/* Rocket + fire (Phase E). Flame scales with recent thrust power
            and shifts colour toward buy-green when climbing, sell-red when
            dipping. A continuous CSS flicker keeps it alive at rest.
            A floating price pill trails to the right of the rocket. */}
        <g className={`rc-rocket rc-rocket-${rocketTone} ${thrustClass}`}
           style={{ transform: `translate(${rocketX}px, ${rocketY}px)` }}>
          {(() => {
            // Flame scale ranges from 0.7 (idle) → 1.6 (max thrust). Non-
            // linear so small trades still look punchy.
            const s = 0.7 + 0.9 * (thrust.power || 0);
            const outerColor = rocketTone === 'up' ? '#B4FF00' : rocketTone === 'down' ? '#FF5470' : '#FF7B4E';
            const innerColor = '#FFD97D';
            return (
              <g className="rc-flame-wrap" style={{ transform: `scale(1, ${s})` }}>
                <path d="M -8 10 Q 0 30 8 10 Q 4 18 0 34 Q -4 18 -8 10 Z"
                  fill={outerColor} className="rc-flame rc-flame-outer" opacity="0.55" />
                <path d="M -5 10 Q 0 24 5 10 Q 2 16 0 27 Q -2 16 -5 10 Z"
                  fill={outerColor} className="rc-flame rc-flame-mid" />
                <path d="M -2.6 10 Q 0 20 2.6 10 Q 1 14 0 22 Q -1 14 -2.6 10 Z"
                  fill={innerColor} className="rc-flame rc-flame-inner" />
              </g>
            );
          })()}
          {/* Rocket body — rendered as SVG <text> instead of a
              <foreignObject><div>🚀</div>. Reason: on iOS Safari / macOS
              Safari (WebKit), <foreignObject> combined with a parent
              CSS `transform` reliably drops HTML content — the emoji
              vanishes and users see only the flame glow. SVG <text>
              renders the system emoji glyph natively on every engine
              (Blink pulls Segoe/Noto Color Emoji, WebKit pulls Apple
              Color Emoji), so the rocket looks identical on Windows,
              Android, iOS, and macOS. The wrapper <g> keeps the same
              .rc-rocket-emoji class so all existing hover / thrust
              jolts + drop-shadow tinting keep working untouched.
              `pointerEvents: none` avoids the emoji swallowing pinch
              gestures on the chart. */}
          <g className="rc-rocket-emoji" style={{ pointerEvents: 'none' }}>
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="22"
              // The emoji glyph itself carries color — the fill here is
              // only used as a fallback for engines that don't render
              // color emoji (very old Firefox on Linux, e.g.).
              fill="#FFFFFF"
            >🚀</text>
          </g>
          {/* Floating price label — small pill trailing right of the rocket. */}
          {currentPrice > 0 && !isPrelaunch && (() => {
            const label = currentPrice >= 1000
              ? `${(currentPrice / 1000).toFixed(currentPrice >= 10000 ? 0 : 1)}K`
              : currentPrice >= 100
                ? currentPrice.toFixed(1)
                : currentPrice >= 10
                  ? currentPrice.toFixed(2)
                  : currentPrice.toFixed(3);
            const charW = 6.6;
            const padding = 8;
            const w = Math.max(46, label.length * charW + padding * 2 + 24 /* pts label */);
            const priceColor = rocketTone === 'up' ? '#B4FF00' : rocketTone === 'down' ? '#FF5470' : '#B4FF00';
            return (
              <g className={`rc-price-tag rc-price-tag-${rocketTone}`}
                 transform="translate(22, -6)">
                <rect x="0" y="-10" width={w} height="20" rx="10" ry="10"
                  fill="rgba(11, 13, 18, 0.85)"
                  stroke={priceColor}
                  strokeWidth="1.2" />
                <text x={padding} y="4" fill={priceColor} fontSize="11" fontWeight="800"
                  fontFamily="'JetBrains Mono', monospace"
                  letterSpacing="0.02em"
                  data-testid="rc-price-tag-text">
                  {label} <tspan opacity="0.6" fontSize="9">pts</tspan>
                </text>
              </g>
            );
          })()}
        </g>

        {/* Confetti particles on trade */}
        {confetti.map((p) => (
          <circle key={p.id} cx={p.x} cy={p.y} r={p.r} fill={p.color}
            className="tp-confetti" style={{ animationDuration: `${p.dur}ms`, transformOrigin: `${p.x}px ${p.y}px` }} />
        ))}
      </svg>

      {/* Empty-state / quiet-window overlay — suppressed during pre-launch.
          Distinguishes 3 scenarios:
            • pre-launch (own countdown overlay)
            • never-traded (currentSupply = 0) → "Waiting for first trades"
            • quiet window (synthetic flat line only) → "No trades in this
              window" chip
            • no trail at all (currentSupply > 0 but backend gave no data
              AND currentPrice is 0) → "No trades in this window" fallback */}
      {!isPrelaunch && (!hasTrail || isQuietWindow) && (
        <div className="rc-empty-hint" data-testid="rc-empty-hint">
          {currentSupply > 0 || isQuietWindow ? (
            <>
              <div>No trades in the {timeframe} window</div>
              <div className="rc-empty-sub">Try a wider timeframe — ALL shows every trade since launch.</div>
            </>
          ) : (
            <>
              <div>Waiting for the first trades…</div>
              <div className="rc-empty-sub">Rocket ready — the first buy launches the run.</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
